import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { XMarkIcon, ArrowPathIcon, CheckIcon, ScaleIcon } from '@heroicons/react/24/outline';
import {
  COUNTDOWN_MS,
  TEAMS,
  TEAM_COUNT_OPTIONS,
  TEAM_SIZE_OPTIONS,
  PickerMode,
  TeamSetup,
  initialPickerState,
  minFingersFor,
  pickerReducer,
  startingPositionsFrom,
  teamCountFor,
} from '../utils/fingerPicker';
import { sendLivePlayMetadataUpdates } from '../utils/livePlayMetadata';

export interface FingerPickerLocationState {
  teams: { teamName: string; playerNames: string[] }[];
  livePlayId?: string;
}

const FINGER_SIZE = 96; // px, comfortably bigger than a fingertip

const SETUP_OPTIONS: { setup: TeamSetup; label: string }[] = [
  ...TEAM_COUNT_OPTIONS.map((value) => ({ setup: { kind: 'count', value } as TeamSetup, label: `${value} teams` })),
  ...TEAM_SIZE_OPTIONS.map((value) => ({ setup: { kind: 'size', value } as TeamSetup, label: `Teams of ${value}` })),
];

const sameSetup = (a: TeamSetup, b: TeamSetup) => a.kind === b.kind && a.value === b.value;

/** Pill toggle on the dark picker background (44px tall for thumbs). */
const pillClass = (active: boolean) =>
  `min-h-[44px] px-4 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
    active ? 'bg-stone-100 text-stone-900' : 'bg-stone-100/10 text-stone-200 hover:bg-stone-100/20'
  }`;

function vibrate(pattern: number | number[]) {
  try {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) navigator.vibrate(pattern);
  } catch {
    // Unsupported or blocked: vibration is a nicety.
  }
}

function usePrefersReducedMotion(): boolean {
  const query = '(prefers-reduced-motion: reduce)';
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(query).matches
  );
  useEffect(() => {
    if (!window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = () => setReduced(mql.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);
  return reduced;
}

/** Keep the screen on while the picker is open, where supported. */
function useWakeLock() {
  useEffect(() => {
    const nav = navigator as Navigator & { wakeLock?: { request: (type: 'screen') => Promise<{ release: () => Promise<void> }> } };
    if (!nav.wakeLock) return;
    let lock: { release: () => Promise<void> } | null = null;
    let cancelled = false;
    const acquire = () => {
      nav.wakeLock!.request('screen').then(
        (l) => {
          if (cancelled) l.release().catch(() => {});
          else lock = l;
        },
        () => {}
      );
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') acquire();
    };
    acquire();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      lock?.release().catch(() => {});
    };
  }, []);
}

const FingerPickerPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as FingerPickerLocationState | null;
  const teams = useMemo(() => navState?.teams ?? [], [navState?.teams]);
  const livePlayId = navState?.livePlayId ?? '';

  const [state, dispatch] = useReducer(pickerReducer, initialPickerState);
  const [showTeamChooser, setShowTeamChooser] = useState(false);
  const [savedTeam, setSavedTeam] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  useWakeLock();

  // Fire the pick when the countdown deadline passes; any finger change
  // produces a new deadline, which cancels this timer and starts a new one.
  useEffect(() => {
    if (state.phase !== 'counting' || state.deadline === null) return;
    const timer = window.setTimeout(
      () => dispatch({ type: 'tick', now: Date.now() }),
      Math.max(0, state.deadline - Date.now())
    );
    return () => window.clearTimeout(timer);
  }, [state.phase, state.deadline]);

  useEffect(() => {
    if (state.phase === 'picked') vibrate([60, 40, 160]);
  }, [state.phase]);

  // iOS Safari ignores touch-action for pinch-zoom in some versions and shows
  // the long-press callout; block both on the touch surface.
  useEffect(() => {
    const el = surfaceRef.current;
    if (!el) return;
    const prevent = (e: Event) => e.preventDefault();
    el.addEventListener('touchstart', prevent, { passive: false });
    el.addEventListener('touchmove', prevent, { passive: false });
    el.addEventListener('contextmenu', prevent);
    el.addEventListener('gesturestart', prevent);
    return () => {
      el.removeEventListener('touchstart', prevent);
      el.removeEventListener('touchmove', prevent);
      el.removeEventListener('contextmenu', prevent);
      el.removeEventListener('gesturestart', prevent);
    };
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (state.phase === 'picked') return;
    dispatch({ type: 'down', id: e.pointerId, x: e.clientX, y: e.clientY, now: Date.now() });
    vibrate(10);
  }, [state.phase]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    dispatch({ type: 'move', id: e.pointerId, x: e.clientX, y: e.clientY });
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    dispatch({ type: 'up', id: e.pointerId, now: Date.now() });
  }, []);

  const again = useCallback(() => {
    dispatch({ type: 'reset' });
    setShowTeamChooser(false);
    setSavedTeam(null);
  }, []);

  const setFirstPlayer = useCallback(async (teamName: string) => {
    if (!livePlayId) return;
    setSaving(true);
    try {
      await sendLivePlayMetadataUpdates(
        livePlayId,
        startingPositionsFrom(teams.map((t) => t.teamName), teamName)
      );
      setSavedTeam(teamName);
      setShowTeamChooser(false);
    } catch (err) {
      console.error('Failed to save first player:', err);
    } finally {
      setSaving(false);
    }
  }, [livePlayId, teams]);

  const setMode = useCallback((mode: PickerMode) => {
    dispatch({ type: 'mode', mode, now: Date.now() });
    setShowTeamChooser(false);
    setSavedTeam(null);
  }, []);

  const setTeamSetup = useCallback((setup: TeamSetup) => {
    dispatch({ type: 'teamSetup', setup, now: Date.now() });
  }, []);

  const winner = state.fingers.find((f) => f.id === state.winnerId) ?? null;
  const teamOf = state.teamOf;
  const count = state.fingers.length;
  const isTeams = state.mode === 'teams';
  const minFingers = minFingersFor(state.mode, state.teamSetup);
  const teamCount = teamCountFor(state.teamSetup, Math.max(count, minFingers));
  const hint =
    state.phase === 'picked'
      ? null
      : count === 0
        ? 'Everyone put a finger on the screen'
        : count < minFingers
          ? isTeams
            ? `Waiting for more fingers… (${minFingers} needed)`
            : 'Waiting for more fingers…'
          : 'Hold still…';

  // Teams mode result, in team order: team index -> finger numbers.
  const teamResults = useMemo(() => {
    if (!teamOf) return [];
    const byTeam: number[][] = [];
    state.fingers.forEach((f) => {
      const t = teamOf[f.id];
      if (t === undefined) return;
      (byTeam[t] = byTeam[t] ?? []).push(f.label);
    });
    return byTeam.map((labels, i) => ({ team: TEAMS[i], labels: labels.sort((a, b) => a - b) }));
  }, [teamOf, state.fingers]);

  return (
    <div
      className="fixed inset-0 z-[60] bg-stone-900 overflow-hidden select-none"
      style={{
        touchAction: 'none',
        overscrollBehavior: 'none',
        WebkitUserSelect: 'none',
        WebkitTouchCallout: 'none',
      } as React.CSSProperties}
    >
      <style>{`
        @keyframes fp-pulse { 0%,100% { transform: translate(-50%,-50%) scale(1); } 50% { transform: translate(-50%,-50%) scale(1.12); } }
        @keyframes fp-ring { from { stroke-dashoffset: 0; } to { stroke-dashoffset: 339.3; } }
        @keyframes fp-win { from { transform: translate(-50%,-50%) scale(1); } to { transform: translate(-50%,-50%) scale(1.6); } }
        @keyframes fp-team { 0% { transform: translate(-50%,-50%) scale(0.8); } 60% { transform: translate(-50%,-50%) scale(1.15); } 100% { transform: translate(-50%,-50%) scale(1); } }
      `}</style>

      {/* Touch surface */}
      <div
        ref={surfaceRef}
        data-testid="finger-surface"
        className="absolute inset-0"
        style={{ touchAction: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onLostPointerCapture={onPointerUp}
      >
        {/* Winner backdrop */}
        {winner && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(circle at ${winner.x}px ${winner.y}px, ${winner.color}55 0, ${winner.color}22 35%, transparent 70%)`,
            }}
          />
        )}

        {/* Team glows */}
        {teamOf && state.fingers.map((f) => {
          const team = TEAMS[teamOf[f.id]];
          if (!team) return null;
          return (
            <div
              key={`glow-${f.id}`}
              className="absolute inset-0 pointer-events-none"
              style={{ background: `radial-gradient(circle at ${f.x}px ${f.y}px, ${team.color}40 0, transparent 160px)` }}
            />
          );
        })}

        {state.fingers.map((f) => {
          const isWinner = f.id === state.winnerId;
          const team = teamOf ? TEAMS[teamOf[f.id]] : undefined;
          const faded = state.phase === 'picked' && !isWinner && !team;
          if (faded) return null;
          const color = team?.color ?? f.color;
          const animation = reducedMotion
            ? undefined
            : isWinner
              ? 'fp-win 400ms ease-out forwards'
              : team
                ? 'fp-team 300ms ease-out'
                : 'fp-pulse 1.2s ease-in-out infinite';
          return (
            <div
              key={f.id}
              data-testid="finger"
              className="absolute pointer-events-none flex items-center justify-center rounded-full"
              style={{
                left: f.x,
                top: f.y,
                width: FINGER_SIZE,
                height: FINGER_SIZE,
                transform: 'translate(-50%,-50%)',
                border: `6px solid ${color}`,
                background: `${color}${team ? '66' : '33'}`,
                boxShadow: `0 0 24px ${color}88`,
                animation,
              }}
            >
              {team ? (
                <span className="flex flex-col items-center leading-none" style={{ color: '#fff' }}>
                  <span className="text-3xl font-black">{team.letter}</span>
                  <span className="text-xs font-semibold mt-0.5">{team.name}</span>
                </span>
              ) : (
                <span className="text-2xl font-black" style={{ color }}>{f.label}</span>
              )}
            </div>
          );
        })}

        {/* Countdown ring, restarts (keyed on deadline) whenever fingers change */}
        {state.phase === 'counting' && (
          <svg
            key={state.deadline ?? 0}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-60"
            width="120" height="120" viewBox="0 0 120 120" aria-hidden="true"
          >
            <circle cx="60" cy="60" r="54" fill="none" stroke="#57534e" strokeWidth="6" />
            <circle
              cx="60" cy="60" r="54" fill="none" stroke="#fafaf9" strokeWidth="6"
              strokeDasharray="339.3" strokeLinecap="round" transform="rotate(-90 60 60)"
              style={{ animation: reducedMotion ? undefined : `fp-ring ${COUNTDOWN_MS}ms linear forwards` }}
            />
          </svg>
        )}
      </div>

      {/* Header */}
      <div style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
        className="absolute top-0 inset-x-0 p-4 pointer-events-none space-y-3">
        <div className="flex items-start justify-between">
          <h1 className="text-white text-fluid-h2 font-bold font-serif mt-1">{isTeams ? 'Make teams' : 'Who starts?'}</h1>
          <button
            onClick={() => navigate(-1)}
            aria-label="Close"
            className="pointer-events-auto p-2 bg-stone-100/10 hover:bg-primary-500/20 rounded-full text-white transition-colors"
          >
            <XMarkIcon className="h-8 w-8" />
          </button>
        </div>

        {state.phase !== 'picked' && (
          <div className="pointer-events-auto space-y-2">
            <div role="radiogroup" aria-label="Mode" className="inline-flex gap-1 p-1 rounded-full bg-stone-100/10">
              {(['pick', 'teams'] as PickerMode[]).map((mode) => (
                <button
                  key={mode}
                  role="radio"
                  aria-checked={state.mode === mode}
                  data-track={`finger_picker_mode_${mode}`}
                  onClick={() => setMode(mode)}
                  className={pillClass(state.mode === mode)}
                >
                  {mode === 'pick' ? 'Who starts' : 'Teams'}
                </button>
              ))}
            </div>

            {isTeams && (
              <>
                <div role="radiogroup" aria-label="Team setup" className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1">
                  {SETUP_OPTIONS.map(({ setup, label }) => (
                    <button
                      key={label}
                      role="radio"
                      aria-checked={sameSetup(state.teamSetup, setup)}
                      onClick={() => setTeamSetup(setup)}
                      className={pillClass(sameSetup(state.teamSetup, setup))}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  data-track="finger_picker_balance_by_rating"
                  onClick={() => navigate('/tools/fair-teams', { state: { teamCount } })}
                  className="flex items-center gap-2 min-h-[44px] text-sm font-semibold text-stone-200 hover:text-white"
                >
                  <ScaleIcon className="h-5 w-5" />
                  Balance by rating instead
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {hint && (
        <p
          role="status"
          className="absolute bottom-10 inset-x-0 text-center text-stone-200 text-lg font-medium pointer-events-none px-6"
        >
          {hint}
        </p>
      )}

      {/* Teams result: list under the title, Again at the bottom, so the
          team dots in the middle stay visible */}
      {teamOf && (
        <div
          role="status"
          style={{ top: 'calc(max(1rem, env(safe-area-inset-top)) + 4rem)' }}
          className="absolute inset-x-4 mx-auto max-w-sm bg-stone-950/85 rounded-2xl px-4 py-3 border border-stone-600 pointer-events-none"
        >
          <div className="text-2xl font-black font-serif text-white mb-2">{teamResults.length} teams!</div>
          <ul className="space-y-1.5">
              {teamResults.map(({ team, labels }) => (
                <li key={team.letter} className="flex items-center gap-3 text-stone-100">
                  <span
                    className="w-7 h-7 rounded-full flex items-center justify-center font-black text-stone-900 shrink-0"
                    style={{ background: team.color }}
                    aria-hidden="true"
                  >
                    {team.letter}
                  </span>
                  <span className="font-semibold" style={{ color: team.color }}>Team {team.name}</span>
                  <span className="ml-auto text-stone-300 text-sm">
                    {labels.length} {labels.length === 1 ? 'finger' : 'fingers'}
                  </span>
                </li>
              ))}
          </ul>
        </div>
      )}
      {teamOf && (
        <div className="absolute bottom-0 inset-x-0 pb-8 px-6 flex justify-center">
          <button
            onClick={again}
            className="flex items-center gap-2 min-h-[44px] px-5 py-3 bg-stone-600 hover:bg-stone-700 text-white rounded-2xl font-bold text-base shadow-2xl"
          >
            <ArrowPathIcon className="h-5 w-5" />
            Again
          </button>
        </div>
      )}

      {/* Result */}
      {winner && (
        <div className="absolute bottom-0 inset-x-0 pb-8 px-6 flex flex-col items-center gap-4">
          <div role="status" className="bg-stone-950/80 rounded-2xl px-6 py-4 text-center border-2" style={{ borderColor: winner.color }}>
            <div className="text-4xl font-black font-serif" style={{ color: winner.color }}>
              Finger {winner.label} starts!
            </div>
            {savedTeam && (
              <div className="text-stone-200 mt-1 text-base">{savedTeam} is set as first player</div>
            )}
          </div>

          {showTeamChooser && (
            <div className="w-full max-w-sm bg-stone-800 rounded-2xl p-3 space-y-2">
              <div className="text-stone-200 text-sm text-center">Whose finger was it?</div>
              {teams.map((t) => (
                <button
                  key={t.teamName}
                  disabled={saving}
                  onClick={() => setFirstPlayer(t.teamName)}
                  className="w-full min-h-[44px] px-4 py-2 rounded-xl bg-stone-700 hover:bg-stone-600 text-white font-semibold disabled:opacity-50"
                >
                  {t.teamName}
                  {t.playerNames.length > 1 || t.playerNames[0] !== t.teamName
                    ? <span className="text-stone-300 font-normal"> · {t.playerNames.join(', ')}</span>
                    : null}
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={again}
              className="flex items-center gap-2 min-h-[44px] px-5 py-3 bg-stone-600 hover:bg-stone-700 text-white rounded-2xl font-bold text-base shadow-2xl"
            >
              <ArrowPathIcon className="h-5 w-5" />
              Again
            </button>
            {livePlayId && teams.length > 0 && !savedTeam && (
              <button
                onClick={() => setShowTeamChooser((v) => !v)}
                className="flex items-center gap-2 min-h-[44px] px-5 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl font-bold text-base shadow-2xl"
              >
                <CheckIcon className="h-5 w-5" />
                Set as first player
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FingerPickerPage;
